import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Badge } from '@dev-go4ellis/go4-design-system';
import './CostEstimator.scss';

const FEE_PERCENTAGE = 0.175;

const formatMoney = (n) => {
  const num = Number(n) || 0;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

class CostEstimator extends Component {
  state = {
    zip: '',
    hours: 4,
    trainers: 1,
    rate: 65,
  };

  onZipChange = (e) => {
    const zip = e.target.value.replace(/\D/g, '').slice(0, 5);
    this.setState({ zip });
  };

  onNumberChange = (field) => (e) => {
    const raw = e.target.value;
    if (raw === '') {
      this.setState({ [field]: '' });
      return;
    }
    const value = Number(raw);
    if (!Number.isNaN(value) && value >= 0) {
      this.setState({ [field]: value });
    }
  };

  step = (field, delta, min = 1) => () => {
    const current = Number(this.state[field]) || min;
    const next = Math.max(min, current + delta);
    this.setState({ [field]: next });
  };

  calculate = () => {
    const hours = Number(this.state.hours) || 0;
    const trainers = Number(this.state.trainers) || 0;
    const rate = Number(this.state.rate) || 0;

    const basePay = hours * trainers * rate;
    const fee = basePay * FEE_PERCENTAGE;
    const total = basePay + fee;

    return { basePay, fee, total };
  };

  render() {
    const { zip, hours, trainers, rate } = this.state;
    const { basePay, fee, total } = this.calculate();

    return (
      <section id={this.props.id} className="pl-estimator">
        <div className="pl-estimator__inner">
          <div className="pl-estimator__badge">
            <Badge variant="soft" color="info" size="sm">COST ESTIMATOR</Badge>
          </div>

          <h2 className="pl-estimator__title">Price your event now</h2>
          <p className="pl-estimator__subtitle">
            Enter your ZIP code and we'll show what athletic trainers in your
            area have actually been paid, then build your estimate from there.
            Go4 does not set rates — you choose the rate.
          </p>

          <div className="pl-estimator__panel">
            <div className="pl-estimator__inputs">
              <div className="pl-estimator__field">
                <label className="pl-estimator__label">ZIP Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 10001"
                  className="pl-estimator__input"
                  value={zip}
                  onChange={this.onZipChange}
                />
                {zip.length === 5 && (
                  <div className="pl-estimator__hint">
                    Avg. rate lookup coming — pick your rate below for now.
                  </div>
                )}
              </div>

              <div className="pl-estimator__field-row">
                <div className="pl-estimator__field">
                  <label className="pl-estimator__label">Hours</label>
                  <div className="pl-estimator__stepper">
                    <button type="button" className="pl-estimator__step-btn" onClick={this.step('hours', -1, 1)} aria-label="Decrease hours">−</button>
                    <input type="number" min="1" className="pl-estimator__input pl-estimator__input--stepper" value={hours} onChange={this.onNumberChange('hours')} />
                    <button type="button" className="pl-estimator__step-btn" onClick={this.step('hours', 1, 1)} aria-label="Increase hours">+</button>
                  </div>
                </div>

                <div className="pl-estimator__field">
                  <label className="pl-estimator__label">Trainers</label>
                  <div className="pl-estimator__stepper">
                    <button type="button" className="pl-estimator__step-btn" onClick={this.step('trainers', -1, 1)} aria-label="Decrease trainers">−</button>
                    <input type="number" min="1" className="pl-estimator__input pl-estimator__input--stepper" value={trainers} onChange={this.onNumberChange('trainers')} />
                    <button type="button" className="pl-estimator__step-btn" onClick={this.step('trainers', 1, 1)} aria-label="Increase trainers">+</button>
                  </div>
                </div>
              </div>

              <div className="pl-estimator__field">
                <label className="pl-estimator__label">Rate / hr</label>
                <div className="pl-estimator__rate-input">
                  <span className="pl-estimator__currency">$</span>
                  <input type="number" min="0" step="1" className="pl-estimator__input pl-estimator__input--rate" value={rate} onChange={this.onNumberChange('rate')} />
                </div>
              </div>
            </div>

            <div className="pl-estimator__results">
              <div className="pl-estimator__result-row">
                <span className="pl-estimator__result-label">Trainer pay</span>
                <span className="pl-estimator__result-value">{formatMoney(basePay)}</span>
              </div>
              <div className="pl-estimator__result-row">
                <span className="pl-estimator__result-label">Service fee (17.5%)</span>
                <span className="pl-estimator__result-value">{formatMoney(fee)}</span>
              </div>
              <div className="pl-estimator__result-divider" />
              <div className="pl-estimator__result-row pl-estimator__result-row--total">
                <span className="pl-estimator__result-label">Estimated total</span>
                <span className="pl-estimator__result-value">{formatMoney(total)}</span>
              </div>

              <div className="pl-estimator__cta-wrap">
                <Button variant="primary" size="lg" fluid onClick={this.props.onPostJobClick}>
                  Post this job free →
                </Button>
              </div>

              <p className="pl-estimator__disclaimer">
                Posting costs nothing. You're only charged after a trainer works the shift.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

CostEstimator.propTypes = {
  id: PropTypes.string,
  onPostJobClick: PropTypes.func.isRequired,
};

CostEstimator.defaultProps = {
  id: undefined,
};

export default CostEstimator;
